/**
 * Custom Events Tracking — geolocation modal and country selection.
 *
 * country_selector_open · country_selector_change
 *
 * Kept in its own file rather than in assets/analytics.js because, uniquely
 * among the tracked events, none of this DOM belongs to the theme. Orbe
 * Geolocation injects both the country selectors and the geolocation modal from
 * its own app embed (config/settings_data.json); the theme renders only the
 * empty #orbe-market-* mount points. These are the only hooks that a
 * third-party app update can break, which is why they are isolated here.
 *
 * Built against Orbe's real markup, supplied from the live storefront during QA
 * (2026-08-28). Two rules do the work:
 *
 *   1. Every country selector — header, footer, drawer — is the same combobox
 *      button with aria-expanded, so that attribute drives the open event and no
 *      click heuristics are needed. All of them report type "dropdown"; "modal"
 *      is reserved for the separate geolocation popup.
 *
 *   2. A change is only a change if the country actually differs from the one the
 *      shopper is already on. The toggle carries data-value with the CURRENT
 *      country, which is why merely closing the dropdown used to look like a
 *      selection.
 *
 * The selectors can be overridden without a theme deploy by setting
 * window.WhittardAnalyticsConfig (orbeToggle, orbeOption, orbeModal, orbeApply)
 * before assets/analytics.js runs.
 */
(function () {
  'use strict';

  var W = window;
  /* Refuse a second execution of this file. The `inited` guard below is
     module-scoped, so it cannot see a duplicate <script> include — and every
     hook here would then be registered twice, silently doubling every event.
     Mirrors the dataset.*Ready guards the theme's own controllers use. */
  if (W.__whittardAnalyticsCountry) return;
  W.__whittardAnalyticsCountry = true;
  var A = W.WhittardAnalytics;
  if (!A) return;
  var push = A.push;
  var CONFIG = A.config;
  /* One body observer serves all three files — see assets/analytics.js. */
  var onDomChange = A.onDomChange;

  var ISO_RE = /^[A-Z]{2}$/;

  /* The market the shopper is already on, rendered by layout/theme.liquid from
     localization.country. Everything below is measured against this. */
  var CURRENT = ((document.body && document.body.getAttribute('data-market-country')) || '').toUpperCase();

  /* Presence is not visibility: Orbe keeps its modal in the DOM and shows or
     hides it, so firing on presence alone would report an open on page load for
     a modal nobody has seen.

     Measured by rendered-ness, not by having a box. An earlier version required
     client rects, which silently failed when the matched element was an outer
     wrapper with no box of its own — the modal opened and nothing was reported
     (QA, Nahuel, 2026-08-28). checkVisibility accounts for the element and its
     ancestors without that assumption; the computed-style path is the fallback
     for browsers that lack it. */
  function isVisible(el) {
    if (!el || !el.isConnected) return false;
    if (typeof el.checkVisibility === 'function') {
      return el.checkVisibility({ checkOpacity: true, checkVisibilityCSS: true });
    }
    var cs = W.getComputedStyle(el);
    if (!cs) return false;
    if (cs.display === 'none' || cs.visibility === 'hidden') return false;
    if (parseFloat(cs.opacity) === 0) return false;
    return true;
  }

  /* The selected country as an ISO 3166-1 alpha-2 code. Orbe exposes it
     differently depending on which control was used, so every plausible carrier
     is checked before giving up rather than reporting a wrong code. */
  function countryFrom(el) {
    var node = el;
    while (node && node.nodeType === 1) {
      /* data-country first: Orbe puts it on both the option row and its inner
         link, and unlike data-value it never appears on the toggle. */
      var attrs = ['data-country', 'data-country-code', 'data-iso', 'data-value', 'value'];
      for (var i = 0; i < attrs.length; i++) {
        var v = (node.getAttribute && node.getAttribute(attrs[i])) || '';
        v = v.trim().toUpperCase();
        if (ISO_RE.test(v)) return v;
      }
      node = node.parentElement;
    }
    var select = document.querySelector('select[name*="country" i], select[name*="market" i]');
    if (select && select.value) {
      var sv = select.value.trim().toUpperCase();
      if (ISO_RE.test(sv)) return sv;
    }
    return '';
  }

  /* Where the interaction happened, which is what `type` means: a country picked
     inside the geolocation modal is "modal" however that modal was opened —
     automatically on arrival, or from the footer trigger — and only the header's
     own inline selector is "dropdown". */
  function inModal(el) {
    return !!(el && el.closest && el.closest(CONFIG.orbeModal));
  }

  /* The outermost matching ancestor, so the visibility watch is attached to the
     modal's own root rather than to whichever inner wrapper matched first. */
  function outermost(el, selector) {
    var node = el;
    var best = el;
    while (node && node.nodeType === 1) {
      if (node.matches && node.matches(selector)) best = node;
      node = node.parentElement;
    }
    return best;
  }

  /* Rule 2. Closing a selector, or re-picking the market already in use,
     resolves to the current country and is not a selection. */
  function reportChange(type, code) {
    if (!code || code === CURRENT) return;
    push('country_selector_change', { type: type, country: code });
  }

  /* ── Country dropdowns ───────────────────────────────────────────────────
     Header, footer and drawer are all the same control: an Orbe combobox button
     whose aria-expanded is toggled, sitting above a listbox of
     li[id^="orbe-country-"] options. aria-expanded is authoritative for opening
     and closing however the shopper triggers it, so no click heuristics are
     needed any more.

     All three report type "dropdown". The spec only contemplates the header one,
     but the footer control is a dropdown by any reading, and calling it a modal
     would be plainly wrong — the modal is the separate arrival popup below.
  ────────────────────────────────────────────────────────────────────────── */

  /* The country picked inside the modal but not yet applied. Read by
     watchModal()'s apply handler, which is what actually reports it. */
  var pendingModalCountry = '';

  function watchDropdowns() {
    onDomChange(function () {
      document.querySelectorAll(CONFIG.orbeToggle).forEach(function (toggle) {
        if (toggle.__countryWatched) return;
        toggle.__countryWatched = true;
        /* A combobox inside the modal is part of the modal, not a dropdown of its
           own — the modal's own open already reported the interaction. */
        if (inModal(toggle)) return;
        var wasOpen = toggle.getAttribute('aria-expanded') === 'true';
        new MutationObserver(function () {
          var isOpen = toggle.getAttribute('aria-expanded') === 'true';
          if (isOpen === wasOpen) return;
          wasOpen = isOpen;
          if (isOpen) push('country_selector_open', { type: 'dropdown' });
        }).observe(toggle, { attributes: true, attributeFilter: ['aria-expanded'] });
      });
    });

    /* A country choice applies immediately — there is no confirm step, unlike the
       modal. Scoped to the option rows so that clicking the toggle, which carries
       data-value with the CURRENT country, can never read as a selection. */
    document.addEventListener('click', function (e) {
      var option = e.target.closest(CONFIG.orbeOption);
      if (!option) return;
      /* Inside the modal a pick is only a selection: the spec is explicit that the
         modal reports its change when the country is APPLIED, on "Shop now".
         watchModal() owns that, and remembers this pick for it. */
      if (inModal(option)) {
        pendingModalCountry = countryFrom(option) || pendingModalCountry;
        return;
      }
      reportChange('dropdown', countryFrom(option));
    }, true);
  }

  /* ── Geolocation modal ───────────────────────────────────────────────────
     Watched as an open/close state machine rather than a one-shot sighting: the
     footer flag can open it repeatedly, and a one-shot watcher reported only the
     first of those.
  ────────────────────────────────────────────────────────────────────────── */

  function watchModal() {
    var modalEl = null;
    var modalOpen = false;
    var applyBound = false;

    /* Reports the modal opening however it happened: injected already-visible on
       first landing, or shown later from the footer trigger. Both reach here — the
       first through findModal()'s initial sync, the second through the scoped
       attribute observer. */
    function syncModal() {
      var open = isVisible(modalEl);
      if (open === modalOpen) return;
      modalOpen = open;
      if (open) push('country_selector_open', { type: 'modal' });
      else pendingModalCountry = '';
    }

    function bindApply(modal) {
      if (applyBound) return;
      applyBound = true;
      /* Spec: the modal reports the change when the country is APPLIED, i.e. on
         "Shop now" — not when one is merely highlighted in its dropdown. A
         "continue where you are" button never matches, and would not pass the
         differs-from-current guard even if it did. */
      modal.addEventListener('click', function (e) {
        var action = e.target.closest(CONFIG.orbeApply);
        if (!action) return;
        if ((action.textContent || '').trim().toLowerCase().indexOf('shop') === -1) return;
        /* In priority order: the option the shopper actually picked in this modal,
           then the modal's own combobox value, then the hidden field Orbe writes
           the applied country into. */
        var code = pendingModalCountry;
        if (!code) {
          var combo = modal.querySelector('[role="combobox"][data-value]');
          if (combo) code = (combo.getAttribute('data-value') || '').trim().toUpperCase();
        }
        if (!code) {
          /* Both shapes: the hidden input Orbe writes the applied country into,
             and a plain <select> if the modal ever renders one instead. */
          var field = modal.querySelector('input[name*="country" i], select[name*="country" i]');
          if (field) code = (field.value || '').trim().toUpperCase();
        }
        if (!ISO_RE.test(code || '')) code = countryFrom(modal);
        reportChange('modal', code);
        pendingModalCountry = '';
      }, true);
    }

    function findModal() {
      if (modalEl && document.contains(modalEl)) return;
      var found = document.querySelector(CONFIG.orbeModal);
      if (!found) return;
      /* The selector matches inner wrappers too, so climb to the modal's own root
         — that is the element whose show/hide actually means "the modal opened". */
      var el = outermost(found, CONFIG.orbeModal);
      modalEl = el;
      applyBound = false;
      bindApply(el);
      /* Watched on the container AND on each of its ancestors, because the app is
         free to hide the modal at any level — a class on an outer wrapper is just
         as likely as one on the container itself, and watching only the container
         missed exactly that case. It is a handful of elements up to <body>, and
         only on the attributes that can show or hide something, so this stays far
         cheaper than widening the shared document sweep to attributes. */
      var watchAttrs = { attributes: true, attributeFilter: ['class', 'style', 'hidden', 'aria-hidden', 'open'] };
      var node = el;
      while (node && node.nodeType === 1) {
        new MutationObserver(syncModal).observe(node, watchAttrs);
        if (node === document.body) break;
        node = node.parentElement;
      }
      syncModal();
    }

    findModal();
    /* The modal is injected after load, so the document has to be watched until
       it appears. */
    onDomChange(function () { findModal(); syncModal(); });
  }

  /* Idempotent and isolated for the same reasons as assets/analytics.js's own
     init — see the notes there on double-counted events and on why one failing
     hook must not take the rest of the tracking down. */
  var inited = false;

  function init() {
    if (inited) return;
    inited = true;
    try {
      watchDropdowns();
      watchModal();
    } catch (e) {
      if (W.console) W.console.warn('[analytics-country] failed to initialise', e);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
